import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Divider,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import NoPaperPageContent from 'components/Generic/NoPaperPageContent';
import { useCharacterizationById, useCharacterizationsWithFilter } from 'hooks';
import Loading from 'components/Loading';
import { formatKgFromG } from 'utils/weight';
import { colors } from 'theme';
import { Routes } from 'routes';
import TableSeparatorRow from 'components/TableSeparatorRow';
import { checkDateUpperTimeInterval, createUTCDateFromDateString, formatDateForDisplay } from 'utils/date';
import { AppContext } from 'context';
import { useLatestClosureDate } from 'utils/closureHooks';
import DeleteConfirmationDialog from 'components/Generic/DeleteConfirmationDialog';
import { notify } from 'components/Notifier';
import { deleteCharacterization, downloadCharacterizationDetailReport } from 'services/apiOperations';
import { goBack } from 'utils/history';
import { UserRole } from '../../types';

const useStyles = makeStyles({
  label: {
    fontWeight: 600,
    marginRight: 16,

    '&:after': {
      content: '" :"',
    },
  },
  characterizationStatus: {
    fontSize: 16,
    zIndex: 1,
    position: 'relative',
    marginLeft: 15,

    '&::before': {
      content: '" "',
      zIndex: -1,
      borderRadius: 100,
      backgroundColor: colors.greenPastel,
      height: 35,
      width: 35,
      position: 'absolute',
      left: -14,
      top: -7,
    },
  },
  characterizationRefused: {
    '&::before': {
      backgroundColor: colors.redPastel,
    },
  },
  tablePaper: {
    maxWidth: '55%',
    borderLeft: `6px solid ${colors.greenPastel}`,
  },
  noBorder: {
    borderBottom: 'none',
  },
  streamPaper: {
    maxWidth: '30%',
    borderLeft: `6px solid ${colors.greenPastel}`,
  },
  subMaterialLabel: {
    fontSize: '12px',
  },
  subMaterialNameCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    borderBottom: 'none',
    fontSize: '12px',
  },
  subMaterialWeightCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    borderBottom: 'none',
    fontSize: '12px',
  },
});

const CharacterizationShow: React.FC = () => {
  const classes = useStyles();

  const history = useHistory();

  const { characterizationId } = useParams<{ characterizationId: string }>();

  const { t } = useTranslation('translation', { keyPrefix: 'characterizationForm' });

  const { value: characterization, loading } = useCharacterizationById(characterizationId);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);
  const samplingDatetime = characterization ? createUTCDateFromDateString(characterization.samplingDate) : new Date();
  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));
  const [characterizationsByStream, total] = useCharacterizationsWithFilter(null, undefined, {
    client: characterization?.client['@id'],
    stream: characterization?.stream['@id'],
  });
  const canEdit =
    !readOnly && characterization && !loading ? !checkDateUpperTimeInterval(samplingDatetime, closureMonth) : false;
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

  const downloadReport = () => {
    if (!characterization?.id) return;

    downloadCharacterizationDetailReport(characterization.id);
  };

  const hasMoreThanOneCharacterization = (): boolean => {
    return total > 1;
  };

  const canDelete = canEdit && (!characterization?.isTheoreticalComposition || !hasMoreThanOneCharacterization());

  return (
    <>
      {loading && total === -1 ? (
        <Loading />
      ) : (
        characterization &&
        characterizationsByStream && (
          <>
            <NoPaperPageContent
              title={t('show.title')}
              backAction={() => goBack(Routes.Characterizations, history)}
              updateButtonLink={
                canEdit
                  ? {
                      pathname: generatePath(Routes.CharacterizationEdit, { characterizationId }),
                      state: { referer: generatePath(Routes.CharacterizationShow, { characterizationId }) },
                    }
                  : undefined
              }
              deleteButtonAction={canDelete ? () => setConfirmDeleteModalOpen(true) : undefined}
              customButtons={[
                {
                  text: t('show.action.downloadReport'),
                  action: downloadReport,
                },
              ]}
            >
              <DeleteConfirmationDialog
                title={t('delete.confirm')}
                isOpen={confirmDeleteModalOpen}
                setOpen={open => setConfirmDeleteModalOpen(open)}
                confirmDeletion={async () => {
                  setConfirmDeleteModalOpen(false);

                  await deleteCharacterization(characterizationId);
                  notify({
                    message: t('delete.success'),
                    severity: 'success',
                  });
                  goBack(Routes.Characterizations, history);
                }}
              />

              <Box display="flex" paddingTop="80px" />
              <Divider />
              <Box display="flex" paddingTop="26px" paddingBottom="26px" maxWidth="70%">
                <Box flexGrow={1}>
                  <Typography>
                    <span className={classes.label}>{t('client')}</span>
                    {characterization.client.shortName}
                  </Typography>
                </Box>
                <Box marginRight="26px">
                  <Typography>
                    <span className={classes.label}>{t('samplingDate')}</span>
                    {formatDateForDisplay(characterization.samplingDate.toString())}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box display="flex" paddingTop="26px" paddingBottom="26px" maxWidth="70%">
                <Typography>
                  <span className={classes.label}>{t('analyseDate')}</span>
                  {characterization.analyseDate ? formatDateForDisplay(characterization.analyseDate.toString()) : '-'}
                </Typography>
              </Box>

              <Paper square className={classes.streamPaper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('stream')}</TableCell>
                      <TableCell align="right">{t('weightShort')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{characterization.stream.name}</TableCell>
                      <TableCell align="right">{formatKgFromG(characterization.weight)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>

              <Paper square className={classes.tablePaper}>
                <Box display="flex" marginTop="4em" paddingLeft="21px" paddingRight="21px">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('material')}</TableCell>
                        <TableCell>{t('subMaterial')}</TableCell>
                        <TableCell align="right">{t('weightShort')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {characterization.characterizationMaterials.map(characMaterial => {
                        return (
                          <>
                            <TableRow key={`characterizationMaterials.${characMaterial['@id']}`}>
                              <TableCell className={classes.noBorder}>{characMaterial.material.shortName}</TableCell>
                              <TableCell className={classes.noBorder}></TableCell>
                              <TableCell align="right" className={classes.noBorder}>
                                {formatKgFromG(characMaterial.weight)}
                              </TableCell>
                            </TableRow>
                            {characMaterial.characterizationSubMaterials
                              ?.sort((a, b) => a.subMaterial.name.localeCompare(b.subMaterial.name))
                              .map((characSubMaterial, indexSubMaterial) => (
                                <TableRow key={`characterizationSubMaterials.${indexSubMaterial}`}>
                                  <TableCell className={classes.noBorder}></TableCell>
                                  <TableCell className={classes.subMaterialNameCell}>
                                    {
                                      /* ATTENTION à l'ORDER BY: le name se réfère à l'ordre des SubMaterials et le weight à l'ordre des CharacterizationSubMaterials */
                                      characSubMaterial.subMaterial.name
                                    }
                                  </TableCell>
                                  <TableCell align="right" className={classes.subMaterialWeightCell}>
                                    {0 < characSubMaterial.weight ? formatKgFromG(characSubMaterial.weight) : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </>
                        );
                      })}
                      <TableSeparatorRow greyColorScheme={true}>Total</TableSeparatorRow>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell align="right" className={classes.noBorder}>
                          {formatKgFromG(characterization.weight)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
              <Box display="flex" marginTop="4em" paddingBottom="10px" maxWidth="55%">
                <TextField
                  label={t('secteur')}
                  fullWidth
                  variant="outlined"
                  disabled
                  value={characterization.secteur ?? ''}
                />
              </Box>
              <Box display="flex" marginTop="1em" paddingBottom="20px" maxWidth="55%">
                <TextField
                  label={t('comment')}
                  multiline
                  fullWidth
                  rows={6}
                  variant="outlined"
                  disabled
                  value={characterization.comment ?? ''}
                />
              </Box>
            </NoPaperPageContent>
          </>
        )
      )}
    </>
  );
};
export default CharacterizationShow;
